// "Silk environments" at /silk: every Flex, its cluster and SDPs, the VMs
// tagged for them, and the power schedules, as the stacks and runbooks see
// them. Read-only. The data comes from the silk-platform backend plugin
// (packages/backend/src/plugins/silkPlatform), which reads App Config, stack
// outputs and Azure; this page only lays it out.

import { useCallback, useEffect, useState } from 'react';
import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { Progress } from '@backstage/core-components';

// ── The shape /api/silk-platform/environments returns ──────────────────────

type Lifecycle = 'deployed' | 'removed' | 'not-managed';

type Sdp = {
  key: string;
  name: string;
  lifecycle: Lifecycle;
  savedConfiguration: boolean;
  isOnline: boolean;
  environmentDefault: boolean;
  sizing: Record<string, unknown>;
  mnodeTypes: string[];
  storage: 'pv2' | 'vm-local';
  powerControl: 'available' | 'always-on';
  facts: { id?: string; mgmtIp?: string; dataSubnet?: string };
  hosts: Array<{
    name: string;
    vm?: string;
    connected: boolean;
    deploy: boolean;
  }>;
  volumeGroups: Array<{ name: string; volumes: number; sizeGb: number }>;
  mappings: Array<{ host: string; volumeGroups: string[] }>;
  lastModified?: string;
};

type Flex = {
  key: string;
  cloud: string;
  name: string;
  lifecycle: Lifecycle;
  isOnline: boolean;
  environmentDefault: boolean;
  prerequisitesOnly: boolean;
  region?: string;
  clusterId?: string;
  imageVersion?: string;
  cluster: { deploy?: boolean; exists?: boolean; status?: string };
  endpoint: string | null;
  sdps: Sdp[];
};

type Vm = {
  name: string;
  powerState: string;
  connect: boolean;
  sdps: string[];
  defaultSdp: boolean;
  volumeProfile?: string;
  hasHostFacts: boolean;
};

type Schedule = {
  name: string;
  deployed: boolean;
  enabled: boolean;
  timezone?: string;
  sdps: string[] | null;
  offline?: { days: string[]; time: string; enabled: boolean };
  online?: { days: string[]; time: string; enabled: boolean };
  once: Array<{ id: string; action: string; at: string; reason?: string }>;
};

type Environments = {
  readAt: string;
  flexes: Flex[];
  vms: Vm[];
  powerSchedules: Schedule[];
  problems: string[];
};

// ── Styles (the portal's dark theme, as on the Docs page) ──────────────────

const PINK = '#FF286E';
const MUTED = 'rgba(255,255,255,0.6)';
const FAINT = 'rgba(255,255,255,0.35)';

const useStyles = makeStyles({
  page: {
    padding: '48px 56px',
    maxWidth: 1200,
    '@media (max-width: 600px)': { padding: '24px 16px' },
  },
  live: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  liveHead: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    color: '#FFF',
  },
  heading: {
    margin: 0,
    fontSize: '2rem',
    fontWeight: 700,
    color: PINK,
    letterSpacing: '0.01em',
  },
  subtitle: { marginTop: 8, marginBottom: 32, fontSize: '1rem', color: MUTED },
  sectionHeading: {
    margin: '40px 0 16px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: FAINT,
  },
  card: {
    backgroundColor: '#0A1128',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '24px 28px',
    marginBottom: 24,
  },
  cardHead: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 8,
  },
  cardTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#FFF' },
  meta: { color: MUTED, fontSize: '0.85rem', margin: '0 0 16px' },
  tableWrap: { overflowX: 'auto' as const },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    '& th': {
      textAlign: 'left' as const,
      color: FAINT,
      fontWeight: 600,
      padding: '8px 12px 8px 0',
      borderBottom: '1px solid rgba(255,255,255,0.12)',
      whiteSpace: 'nowrap' as const,
    },
    '& td': {
      padding: '10px 12px 10px 0',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      verticalAlign: 'top' as const,
      color: 'rgba(255,255,255,0.85)',
    },
  },
  pill: {
    display: 'inline-block',
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 999,
    marginRight: 6,
    whiteSpace: 'nowrap' as const,
  },
  good: { backgroundColor: 'rgba(80,200,150,0.15)', color: '#5CD6A0' },
  off: { backgroundColor: 'rgba(255,255,255,0.08)', color: MUTED },
  warn: { backgroundColor: 'rgba(255,180,60,0.15)', color: '#F2B44C' },
  accent: { backgroundColor: 'rgba(255,40,110,0.15)', color: '#FF7AA2' },
  problems: {
    padding: '16px 20px',
    borderRadius: 8,
    borderLeft: '3px solid #F2B44C',
    backgroundColor: 'rgba(255,180,60,0.08)',
    color: 'rgba(255,255,255,0.85)',
    marginBottom: 24,
    fontSize: '0.875rem',
    '& ul': { margin: '8px 0 0', paddingLeft: 20 },
  },
  error: {
    padding: '16px 20px',
    borderRadius: 8,
    borderLeft: `3px solid ${PINK}`,
    backgroundColor: 'rgba(255,40,110,0.08)',
    color: '#FFF',
  },
  small: { color: FAINT, fontSize: '0.78rem' },
  refresh: {
    background: 'none',
    border: '1px solid rgba(255,255,255,0.2)',
    color: MUTED,
    borderRadius: 4,
    padding: '4px 12px',
    cursor: 'pointer',
    marginLeft: 12,
    '&:hover': { borderColor: PINK, color: '#FFF' },
  },
  empty: { color: MUTED },
});

type Classes = ReturnType<typeof useStyles>;

const Pill = (props: {
  classes: Classes;
  kind: 'good' | 'off' | 'warn' | 'accent';
  children: React.ReactNode;
}) => (
  <span className={`${props.classes.pill} ${props.classes[props.kind]}`}>
    {props.children}
  </span>
);

// ── Pieces ─────────────────────────────────────────────────────────────────

function sizingSummary(sdp: Sdp): string {
  const s = sdp.sizing;
  if (s.entry_level === true) return 'Entry level';
  const cnodes = `${s.cnode_count ?? '?'} × ${s.cnode_type ?? '?'}`;
  const mnodes =
    s.mnodes && typeof s.mnodes === 'object' && Object.keys(s.mnodes).length > 0
      ? Object.entries(s.mnodes as Record<string, number>)
          .map(([t, n]) => `${n} × ${t}`)
          .join(', ')
      : `${s.mnode_count ?? '?'} × ${s.mnode_type ?? '?'}`;
  return `${cnodes}; ${mnodes}`;
}

const SdpState = ({ sdp, classes }: { sdp: Sdp; classes: Classes }) => {
  if (sdp.savedConfiguration) {
    return (
      <Pill classes={classes} kind="accent">
        saved configuration
      </Pill>
    );
  }
  if (sdp.lifecycle === 'not-managed') {
    return (
      <Pill classes={classes} kind="warn">
        not managed (no deploy)
      </Pill>
    );
  }
  if (sdp.lifecycle === 'removed') {
    return (
      <Pill classes={classes} kind="off">
        being removed
      </Pill>
    );
  }
  return sdp.isOnline ? (
    <Pill classes={classes} kind="good">
      online
    </Pill>
  ) : (
    <Pill classes={classes} kind="off">
      offline
    </Pill>
  );
};

const SdpTable = ({ flex, classes }: { flex: Flex; classes: Classes }) => {
  if (flex.sdps.length === 0) {
    return <p className={classes.empty}>No SDPs on this Flex.</p>;
  }
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>SDP</th>
            <th>State</th>
            <th>Storage and power</th>
            <th>Sizing</th>
            <th>Hosts</th>
            <th>Volume groups</th>
            <th>Address</th>
          </tr>
        </thead>
        <tbody>
          {flex.sdps.map(sdp => (
            <tr key={sdp.key}>
              <td>
                <strong>{sdp.name}</strong>
                {sdp.environmentDefault && (
                  <div className={classes.small}>environment default</div>
                )}
              </td>
              <td>
                <SdpState sdp={sdp} classes={classes} />
              </td>
              <td>
                {sdp.storage === 'pv2' ? (
                  <Pill classes={classes} kind="good">
                    PV2 disks: power on demand
                  </Pill>
                ) : (
                  <Pill classes={classes} kind="off">
                    always on: VM-local storage
                  </Pill>
                )}
              </td>
              <td>{sizingSummary(sdp)}</td>
              <td>
                {sdp.hosts.length === 0
                  ? '—'
                  : sdp.hosts.map(h => (
                      <div key={h.name}>
                        {h.name}
                        {!h.deploy && (
                          <span className={classes.small}>
                            {' '}
                            (not registered)
                          </span>
                        )}
                        {h.deploy && !h.connected && (
                          <span className={classes.small}>
                            {' '}
                            (nothing mapped)
                          </span>
                        )}
                      </div>
                    ))}
              </td>
              <td>
                {sdp.volumeGroups.length === 0
                  ? '—'
                  : sdp.volumeGroups.map(vg => (
                      <div key={vg.name}>
                        {vg.name}{' '}
                        <span className={classes.small}>
                          {vg.volumes} vol, {vg.sizeGb} GB
                        </span>
                      </div>
                    ))}
              </td>
              <td>
                {sdp.facts.mgmtIp ?? '—'}
                {sdp.facts.id && (
                  <div className={classes.small}>id {sdp.facts.id}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Live: the SDP as it is, compared with the tree ─────────────────────────

type ItemState = 'in-sync' | 'differs' | 'not-in-config' | 'missing';

type Comparison = {
  items: Array<{
    kind: string;
    name: string;
    state: ItemState;
    differences: Array<{ field: string; expected: string; actual: string }>;
  }>;
  counts: Record<ItemState, number>;
};

type LiveResult = {
  status:
    | 'compared'
    | 'not-running'
    | 'offline'
    | 'not-deployed'
    | 'unreachable';
  checkedAt: string;
  flexState?: string;
  error?: string;
  comparison?: Comparison;
  lastCompared?: { checkedAt: string; comparison: Comparison };
};

const STATE_TEXT: Record<ItemState, string> = {
  'in-sync': 'in sync',
  differs: 'differs',
  'not-in-config': 'not in config',
  missing: 'missing',
};

const STATE_HINT: Record<Exclude<ItemState, 'in-sync'>, string> = {
  differs: 'the next apply of this SDP puts it back as configured',
  'not-in-config':
    'made outside the automation: nothing manages it, and it stays until someone adopts or removes it',
  missing: 'the next apply of this SDP builds it again',
};

const KIND_TEXT: Record<string, string> = {
  volume_group: 'volume group',
  volume: 'volume',
  host: 'host',
  host_group: 'host group',
  mapping: 'mapping',
};

const ComparisonView = ({
  comparison,
  classes,
}: {
  comparison: Comparison;
  classes: Classes;
}) => {
  const c = comparison.counts;
  const off = comparison.items.filter(i => i.state !== 'in-sync');
  return (
    <>
      <div>
        <Pill classes={classes} kind="good">
          {c['in-sync']} in sync
        </Pill>
        <Pill classes={classes} kind={c.differs ? 'warn' : 'off'}>
          {c.differs} differ
        </Pill>
        <Pill classes={classes} kind={c['not-in-config'] ? 'warn' : 'off'}>
          {c['not-in-config']} not in config
        </Pill>
        <Pill classes={classes} kind={c.missing ? 'warn' : 'off'}>
          {c.missing} missing
        </Pill>
      </div>
      {off.length > 0 && (
        <div className={classes.tableWrap}>
          <table className={classes.table} style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Object</th>
                <th>State</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {off.map(item => (
                <tr key={`${item.kind}:${item.name}`}>
                  <td>
                    <span className={classes.small}>
                      {KIND_TEXT[item.kind] ?? item.kind}{' '}
                    </span>
                    {item.name}
                  </td>
                  <td>
                    <Pill classes={classes} kind="warn">
                      {STATE_TEXT[item.state]}
                    </Pill>
                  </td>
                  <td>
                    {item.differences.map(d => (
                      <div key={d.field}>
                        {d.field}: configured {d.expected || '(empty)'}, on the
                        SDP {d.actual || '(empty)'}
                      </div>
                    ))}
                    <div className={classes.small}>
                      {STATE_HINT[item.state as Exclude<ItemState, 'in-sync'>]}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const LivePanel = ({ sdp, classes }: { sdp: Sdp; classes: Classes }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [result, setResult] = useState<LiveResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (refresh: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('silk-platform');
        const resp = await fetchApi.fetch(
          `${baseUrl}/live?key=${encodeURIComponent(sdp.key)}${
            refresh ? '&refresh=true' : ''
          }`,
        );
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
        setResult((await resp.json()) as LiveResult);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi, sdp.key],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const time = (iso: string) => new Date(iso).toLocaleString();
  let headline: React.ReactNode = null;
  if (result?.status === 'compared') {
    headline = <>Compared with the SDP at {time(result.checkedAt)}.</>;
  } else if (result?.status === 'not-running') {
    headline = (
      <>
        Not running: Flex reports {result.flexState || 'no state'}. Its objects
        cannot be read until it is online.
      </>
    );
  } else if (result?.status === 'offline') {
    headline = (
      <>Offline, as configured: the Flex is off, so the SDP cannot be read.</>
    );
  } else if (result?.status === 'unreachable') {
    headline = <>Could not be read: {result.error}</>;
  } else if (result?.status === 'not-deployed') {
    headline = <>Not deployed.</>;
  }

  return (
    <div className={classes.live}>
      <div className={classes.liveHead}>
        <strong>{sdp.name}: live</strong>
        {result?.flexState && (
          <Pill
            classes={classes}
            kind={result.flexState === 'RUNNING' ? 'good' : 'off'}
          >
            Flex: {result.flexState}
          </Pill>
        )}
        {sdp.isOnline !== (result?.flexState === 'RUNNING') &&
          result?.flexState && (
            <Pill classes={classes} kind="warn">
              config says {sdp.isOnline ? 'online' : 'offline'}
            </Pill>
          )}
        <button
          type="button"
          className={classes.refresh}
          onClick={() => load(true)}
          disabled={loading}
        >
          {loading ? 'Checking…' : 'Check now'}
        </button>
      </div>
      {loading && !result && <Progress />}
      {error && <p className={classes.meta}>Could not check: {error}</p>}
      {headline && <p className={classes.meta}>{headline}</p>}
      {result?.comparison && (
        <ComparisonView comparison={result.comparison} classes={classes} />
      )}
      {!result?.comparison && result?.lastCompared && (
        <>
          <p className={classes.meta}>
            Last compared {time(result.lastCompared.checkedAt)}, when it was
            reachable:
          </p>
          <ComparisonView
            comparison={result.lastCompared.comparison}
            classes={classes}
          />
        </>
      )}
    </div>
  );
};

const FlexCard = ({ flex, classes }: { flex: Flex; classes: Classes }) => {
  const cluster = flex.cluster;
  let clusterState = 'no cluster';
  if (cluster.exists) clusterState = `cluster ${cluster.status ?? 'built'}`;
  else if (cluster.deploy) clusterState = 'cluster not built yet';
  return (
    <div className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>{flex.name}</h2>
        {flex.lifecycle === 'deployed' ? (
          <Pill classes={classes} kind={flex.isOnline ? 'good' : 'off'}>
            {flex.isOnline ? 'online' : 'offline'}
          </Pill>
        ) : (
          <Pill classes={classes} kind="warn">
            {flex.lifecycle === 'removed' ? 'parked or removed' : 'not managed'}
          </Pill>
        )}
        {flex.environmentDefault && (
          <Pill classes={classes} kind="accent">
            environment default
          </Pill>
        )}
      </div>
      <p className={classes.meta}>
        {flex.cloud} {flex.region ?? ''} · {clusterState}
        {flex.clusterId && ` (id ${flex.clusterId})`}
        {flex.imageVersion && ` · image ${flex.imageVersion}`}
        {flex.prerequisitesOnly && ' · prerequisites only'}
        {flex.endpoint && (
          <>
            {' · '}
            <span>{flex.endpoint}</span>
          </>
        )}
      </p>
      <SdpTable flex={flex} classes={classes} />
      {flex.sdps
        .filter(sdp => sdp.lifecycle === 'deployed')
        .map(sdp => (
          <LivePanel key={sdp.key} sdp={sdp} classes={classes} />
        ))}
    </div>
  );
};

function vmState(vm: Vm): 'good' | 'off' | 'warn' {
  if (vm.powerState === 'running') return 'good';
  if (vm.powerState === '' || vm.powerState === 'unknown') return 'warn';
  return 'off';
}

const VmTable = ({ vms, classes }: { vms: Vm[]; classes: Classes }) => {
  if (vms.length === 0) {
    return <p className={classes.empty}>No VMs are tagged for an SDP.</p>;
  }
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>VM</th>
            <th>Power</th>
            <th>SDPs (from its tags)</th>
            <th>Volume profile</th>
            <th>Host facts</th>
          </tr>
        </thead>
        <tbody>
          {vms.map(vm => (
            <tr key={vm.name}>
              <td>{vm.name}</td>
              <td>
                <Pill classes={classes} kind={vmState(vm)}>
                  {vm.powerState || 'unknown'}
                </Pill>
              </td>
              <td>
                {!vm.connect
                  ? 'not connected (no sdp-connect)'
                  : [
                      ...vm.sdps,
                      ...(vm.defaultSdp ? ['default SDP'] : []),
                    ].join(', ') || '—'}
              </td>
              <td>{vm.volumeProfile ?? '—'}</td>
              <td>
                {vm.hasHostFacts ? (
                  'yes'
                ) : (
                  <Pill classes={classes} kind="warn">
                    missing: cannot be registered
                  </Pill>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function weeklyText(w?: { days: string[]; time: string; enabled: boolean }) {
  if (!w) return '—';
  const text = `${w.days.map(d => d.slice(0, 3)).join(', ')} ${w.time}`;
  return w.enabled ? text : `${text} (off)`;
}

const ScheduleTable = ({
  schedules,
  classes,
}: {
  schedules: Schedule[];
  classes: Classes;
}) => {
  if (schedules.length === 0) {
    return <p className={classes.empty}>No power schedules.</p>;
  }
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Schedule</th>
            <th>State</th>
            <th>SDPs</th>
            <th>Weekly offline</th>
            <th>Weekly online</th>
            <th>One-off runs</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map(s => (
            <tr key={s.name}>
              <td>
                <strong>{s.name}</strong>
                {s.timezone && (
                  <div className={classes.small}>{s.timezone}</div>
                )}
              </td>
              <td>
                {!s.deployed ? (
                  <Pill classes={classes} kind="off">
                    removed
                  </Pill>
                ) : (
                  <Pill classes={classes} kind={s.enabled ? 'good' : 'warn'}>
                    {s.enabled ? 'enabled' : 'disabled'}
                  </Pill>
                )}
              </td>
              <td>
                {s.sdps === null
                  ? 'environment default SDP'
                  : s.sdps.join(', ') || 'VMs only'}
              </td>
              <td>{weeklyText(s.offline)}</td>
              <td>{weeklyText(s.online)}</td>
              <td>
                {s.once.length === 0
                  ? '—'
                  : s.once.map(o => (
                      <div key={o.id}>
                        {o.action} at {new Date(o.at).toLocaleString()}
                        {o.reason && (
                          <span className={classes.small}> ({o.reason})</span>
                        )}
                      </div>
                    ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── The page ───────────────────────────────────────────────────────────────

const SilkEnvironmentsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<Environments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('silk-platform');
      const resp = await fetchApi.fetch(`${baseUrl}/environments`);
      if (!resp.ok) {
        // Backstage errors are {error: {message}}; the plugin's 403 is
        // {error: "..."}.
        const body = (await resp.json().catch(() => null)) as {
          error?: string | { message?: string };
        } | null;
        const e = body?.error;
        throw new Error(
          (typeof e === 'string' ? e : e?.message) ??
            `${resp.status} ${resp.statusText}`,
        );
      }
      setData((await resp.json()) as Environments);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={classes.page}>
      <h1 className={classes.heading}>Silk environments</h1>
      <p className={classes.subtitle}>
        Every Flex, its SDPs, the hosts tagged for them and the power schedules,
        as the automation sees them.
        {data && (
          <span className={classes.small}>
            {' '}
            Read {new Date(data.readAt).toLocaleTimeString()}.
          </span>
        )}
        <button
          type="button"
          className={classes.refresh}
          onClick={() => load()}
          disabled={loading}
        >
          Refresh
        </button>
      </p>

      {loading && !data && <Progress />}
      {error && (
        <div className={classes.error}>
          The Silk environments could not be read: {error}
        </div>
      )}

      {data && (
        <>
          {data.problems.length > 0 && (
            <div className={classes.problems}>
              Some of this could not be read, so parts of the page are
              incomplete:
              <ul>
                {data.problems.map(p => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          <p className={classes.sectionHeading}>Flexes and SDPs</p>
          {data.flexes.length === 0 ? (
            <p className={classes.empty}>No Flexes in the Silk tree.</p>
          ) : (
            data.flexes.map(flex => (
              <FlexCard key={flex.key} flex={flex} classes={classes} />
            ))
          )}

          <p className={classes.sectionHeading}>Hosts</p>
          <VmTable vms={data.vms} classes={classes} />

          <p className={classes.sectionHeading}>Power schedules</p>
          <ScheduleTable schedules={data.powerSchedules} classes={classes} />
        </>
      )}
    </div>
  );
};

const silkEnvironmentsPage = PageBlueprint.make({
  name: 'silk-environments',
  params: {
    path: '/silk',
    noHeader: true,
    loader: async () => <SilkEnvironmentsPage />,
  },
});

export const silkEnvironmentsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [silkEnvironmentsPage],
});
